// 9.0.0 signaling format — serialize/deserialize layer for tgcalls InstanceV2
// Reference: tgcalls/v2/Signaling.h + Signaling.cpp
// Pure types + JSON format. No crypto, no RTCPeerConnection.

// --- Types matching native Signaling.h ---

export interface DtlsFingerprint {
  hash: string;
  setup: string;
  fingerprint: string;
}

export interface SsrcGroup {
  semantics: string;
  ssrcs: number[];
}

export interface FeedbackType {
  type: string;
  subtype: string;
}

export interface PayloadType {
  id: number;
  name: string;
  clockrate: number;
  channels: number;
  feedbackTypes: FeedbackType[];
  parameters: Record<string, string>;
}

export interface RtpExtension {
  id: number;
  uri: string;
}

export interface MediaContent {
  type: 'audio' | 'video';
  ssrc: number;
  ssrcGroups: SsrcGroup[];
  payloadTypes: PayloadType[];
  rtpExtensions: RtpExtension[];
}

export interface InitialSetupMessage {
  '@type': 'InitialSetup';
  ufrag: string;
  pwd: string;
  renomination: boolean;
  fingerprints: DtlsFingerprint[];
}

export interface NegotiateChannelsMessage {
  '@type': 'NegotiateChannels';
  exchangeId: string;
  contents: MediaContent[];
}

export interface CandidatesMessage {
  '@type': 'Candidates';
  candidates: Array<{ sdpString: string }>;
}

export type VideoState = 'inactive' | 'active' | 'suspended';
export type VideoRotation = 0 | 90 | 180 | 270;

export interface MediaStateMessage {
  '@type': 'MediaState';
  muted: boolean;
  videoState: VideoState;
  videoRotation: VideoRotation;
  screencastState: VideoState;
  lowBattery: boolean;
}

export type SignalingMessage =
  | InitialSetupMessage
  | NegotiateChannelsMessage
  | CandidatesMessage
  | MediaStateMessage;

// --- Serialize ---
// Native serializes ssrc, ssrcs, and exchangeId as strings (uint32ToString).
// We must match that format exactly for interop.

function serializeSsrcGroup(group: SsrcGroup): Record<string, unknown> {
  return {
    semantics: group.semantics,
    ssrcs: group.ssrcs.map(String),
  };
}

function serializeFeedbackType(ft: FeedbackType): Record<string, string> {
  return {
    type: ft.type,
    subtype: ft.subtype,
  };
}

function serializePayloadType(pt: PayloadType): Record<string, unknown> {
  return {
    id: pt.id,
    name: pt.name,
    clockrate: pt.clockrate,
    channels: pt.channels,
    feedbackTypes: pt.feedbackTypes.map(serializeFeedbackType),
    parameters: pt.parameters,
  };
}

function serializeRtpExtension(ext: RtpExtension): Record<string, unknown> {
  return {
    id: ext.id,
    uri: ext.uri,
  };
}

function serializeMediaContent(content: MediaContent): Record<string, unknown> {
  const obj: Record<string, unknown> = {
    type: content.type,
    ssrc: String(content.ssrc),
    rtpExtensions: content.rtpExtensions.map(serializeRtpExtension),
  };

  // Native only includes ssrcGroups/payloadTypes if non-empty
  if (content.ssrcGroups.length > 0) {
    obj.ssrcGroups = content.ssrcGroups.map(serializeSsrcGroup);
  }
  if (content.payloadTypes.length > 0) {
    obj.payloadTypes = content.payloadTypes.map(serializePayloadType);
  }

  return obj;
}

export function serializeSignalingMessage(msg: SignalingMessage): string {
  switch (msg['@type']) {
    case 'InitialSetup':
      return JSON.stringify({
        '@type': 'InitialSetup',
        ufrag: msg.ufrag,
        pwd: msg.pwd,
        renomination: msg.renomination,
        fingerprints: msg.fingerprints.map((fp) => ({
          hash: fp.hash,
          setup: fp.setup,
          fingerprint: fp.fingerprint,
        })),
      });

    case 'NegotiateChannels':
      return JSON.stringify({
        '@type': 'NegotiateChannels',
        exchangeId: msg.exchangeId,
        contents: msg.contents.map(serializeMediaContent),
      });

    case 'Candidates':
      return JSON.stringify({
        '@type': 'Candidates',
        candidates: msg.candidates.map((c) => ({ sdpString: c.sdpString })),
      });

    case 'MediaState':
      return JSON.stringify({
        '@type': 'MediaState',
        muted: msg.muted,
        lowBattery: msg.lowBattery,
        videoState: msg.videoState,
        videoRotation: msg.videoRotation,
        screencastState: msg.screencastState,
      });
  }
}

// --- Deserialize ---
// Native parser accepts ssrc/ssrcs/exchangeId as string OR number.
// We normalize to our typed representation (numbers internally).

const VALID_VIDEO_STATES = new Set(['inactive', 'active', 'suspended']);
const VALID_ROTATIONS = new Set([0, 90, 180, 270]);

function parseUint32(value: unknown): number | undefined {
  if (typeof value === 'string') {
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) return undefined;
    return n >>> 0; // ensure uint32
  }
  if (typeof value === 'number') return value >>> 0;
  return undefined;
}

function parseSsrcGroup(obj: Record<string, unknown>): SsrcGroup | undefined {
  if (typeof obj.semantics !== 'string') return undefined;

  const rawSsrcs = obj.ssrcs;
  if (!Array.isArray(rawSsrcs)) return undefined;

  const ssrcs: number[] = [];
  for (const s of rawSsrcs) {
    const parsed = parseUint32(s);
    if (parsed === undefined) return undefined;
    ssrcs.push(parsed);
  }

  return { semantics: obj.semantics, ssrcs };
}

function parseFeedbackType(obj: Record<string, unknown>): FeedbackType | undefined {
  if (typeof obj.type !== 'string') return undefined;
  if (typeof obj.subtype !== 'string') return undefined;
  return { type: obj.type, subtype: obj.subtype };
}

function parsePayloadType(obj: Record<string, unknown>): PayloadType | undefined {
  if (typeof obj.id !== 'number') return undefined;
  if (typeof obj.name !== 'string') return undefined;
  if (typeof obj.clockrate !== 'number') return undefined;

  const channels = typeof obj.channels === 'number' ? obj.channels : 0;

  const feedbackTypes: FeedbackType[] = [];
  if (obj.feedbackTypes !== undefined) {
    if (!Array.isArray(obj.feedbackTypes)) return undefined;
    for (const ft of obj.feedbackTypes) {
      if (typeof ft !== 'object' || ft === null) return undefined;
      const parsed = parseFeedbackType(ft as Record<string, unknown>);
      if (!parsed) return undefined;
      feedbackTypes.push(parsed);
    }
  }

  const parameters: Record<string, string> = {};
  if (obj.parameters !== undefined) {
    if (typeof obj.parameters !== 'object' || obj.parameters === null || Array.isArray(obj.parameters)) {
      return undefined;
    }
    for (const [k, v] of Object.entries(obj.parameters as Record<string, unknown>)) {
      if (typeof v !== 'string') return undefined;
      parameters[k] = v;
    }
  }

  return {
    id: obj.id,
    name: obj.name,
    clockrate: obj.clockrate,
    channels,
    feedbackTypes,
    parameters,
  };
}

function parseRtpExtension(obj: Record<string, unknown>): RtpExtension | undefined {
  if (typeof obj.id !== 'number') return undefined;
  if (typeof obj.uri !== 'string') return undefined;
  return { id: obj.id, uri: obj.uri };
}

function parseMediaContent(obj: Record<string, unknown>): MediaContent | undefined {
  if (obj.type !== 'audio' && obj.type !== 'video') return undefined;

  const ssrc = parseUint32(obj.ssrc);
  if (ssrc === undefined) return undefined;

  const ssrcGroups: SsrcGroup[] = [];
  if (obj.ssrcGroups !== undefined) {
    if (!Array.isArray(obj.ssrcGroups)) return undefined;
    for (const g of obj.ssrcGroups) {
      if (typeof g !== 'object' || g === null) return undefined;
      const parsed = parseSsrcGroup(g as Record<string, unknown>);
      if (!parsed) return undefined;
      ssrcGroups.push(parsed);
    }
  }

  const payloadTypes: PayloadType[] = [];
  if (obj.payloadTypes !== undefined) {
    if (!Array.isArray(obj.payloadTypes)) return undefined;
    for (const pt of obj.payloadTypes) {
      if (typeof pt !== 'object' || pt === null) return undefined;
      const parsed = parsePayloadType(pt as Record<string, unknown>);
      if (!parsed) return undefined;
      payloadTypes.push(parsed);
    }
  }

  const rtpExtensions: RtpExtension[] = [];
  if (obj.rtpExtensions !== undefined) {
    if (!Array.isArray(obj.rtpExtensions)) return undefined;
    for (const ext of obj.rtpExtensions) {
      if (typeof ext !== 'object' || ext === null) return undefined;
      const parsed = parseRtpExtension(ext as Record<string, unknown>);
      if (!parsed) return undefined;
      rtpExtensions.push(parsed);
    }
  }

  return {
    type: obj.type,
    ssrc,
    ssrcGroups,
    payloadTypes,
    rtpExtensions,
  };
}

function parseInitialSetup(obj: Record<string, unknown>): InitialSetupMessage | undefined {
  if (typeof obj.ufrag !== 'string') return undefined;
  if (typeof obj.pwd !== 'string') return undefined;
  if (!Array.isArray(obj.fingerprints)) return undefined;

  const renomination = typeof obj.renomination === 'boolean' ? obj.renomination : false;

  const fingerprints: DtlsFingerprint[] = [];
  for (const fp of obj.fingerprints) {
    if (typeof fp !== 'object' || fp === null) return undefined;
    const fpObj = fp as Record<string, unknown>;
    if (typeof fpObj.hash !== 'string') return undefined;
    if (typeof fpObj.setup !== 'string') return undefined;
    if (typeof fpObj.fingerprint !== 'string') return undefined;
    fingerprints.push({
      hash: fpObj.hash,
      setup: fpObj.setup,
      fingerprint: fpObj.fingerprint,
    });
  }

  return {
    '@type': 'InitialSetup',
    ufrag: obj.ufrag,
    pwd: obj.pwd,
    renomination,
    fingerprints,
  };
}

function parseNegotiateChannels(obj: Record<string, unknown>): NegotiateChannelsMessage | undefined {
  const exchangeId = obj.exchangeId;
  // Native accepts string or number, we normalize to string
  let exchangeIdStr: string;
  if (typeof exchangeId === 'string') {
    exchangeIdStr = exchangeId;
  } else if (typeof exchangeId === 'number') {
    exchangeIdStr = String(exchangeId >>> 0);
  } else {
    return undefined;
  }

  const contents: MediaContent[] = [];
  if (obj.contents !== undefined) {
    if (!Array.isArray(obj.contents)) return undefined;
    for (const c of obj.contents) {
      if (typeof c !== 'object' || c === null) return undefined;
      const parsed = parseMediaContent(c as Record<string, unknown>);
      if (!parsed) return undefined;
      contents.push(parsed);
    }
  }

  return {
    '@type': 'NegotiateChannels',
    exchangeId: exchangeIdStr,
    contents,
  };
}

function parseCandidates(obj: Record<string, unknown>): CandidatesMessage | undefined {
  if (!Array.isArray(obj.candidates)) return undefined;

  const candidates: Array<{ sdpString: string }> = [];
  for (const c of obj.candidates) {
    if (typeof c !== 'object' || c === null) return undefined;
    const cObj = c as Record<string, unknown>;
    if (typeof cObj.sdpString !== 'string') return undefined;
    candidates.push({ sdpString: cObj.sdpString });
  }

  return {
    '@type': 'Candidates',
    candidates,
  };
}

function parseMediaState(obj: Record<string, unknown>): MediaStateMessage | undefined {
  const muted = typeof obj.muted === 'boolean' ? obj.muted : false;
  const lowBattery = typeof obj.lowBattery === 'boolean' ? obj.lowBattery : false;

  let videoState: VideoState = 'inactive';
  if (typeof obj.videoState === 'string' && VALID_VIDEO_STATES.has(obj.videoState)) {
    videoState = obj.videoState as VideoState;
  }

  let screencastState: VideoState = 'inactive';
  if (typeof obj.screencastState === 'string' && VALID_VIDEO_STATES.has(obj.screencastState)) {
    screencastState = obj.screencastState as VideoState;
  }

  let videoRotation: VideoRotation = 0;
  if (typeof obj.videoRotation === 'number' && VALID_ROTATIONS.has(obj.videoRotation)) {
    videoRotation = obj.videoRotation as VideoRotation;
  }

  return {
    '@type': 'MediaState',
    muted,
    videoState,
    videoRotation,
    screencastState,
    lowBattery,
  };
}

export function deserializeSignalingMessage(json: string): SignalingMessage | undefined {
  let obj: Record<string, unknown>;
  try {
    const parsed = JSON.parse(json);
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return undefined;
    obj = parsed as Record<string, unknown>;
  } catch {
    return undefined;
  }

  const type = obj['@type'];
  if (typeof type !== 'string') return undefined;

  switch (type) {
    case 'InitialSetup': return parseInitialSetup(obj);
    case 'NegotiateChannels': return parseNegotiateChannels(obj);
    case 'Candidates': return parseCandidates(obj);
    case 'MediaState': return parseMediaState(obj);
    default: return undefined;
  }
}
